import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { writesInFlight } from "./api/client";
import { mountNextIn, takeLeaving } from "./boot";

/*
 * A tab left open runs the console it was opened with (2026-10-01). The owner:
 * 「推了最新的改动但是他们的页面还是旧的」. Admin and CS keep this tab open all day,
 * and the console is one page that never reloads itself, so a tab opened before
 * a deploy goes on running the old build — against an API that has moved on —
 * until somebody happens to press F5. Only the TV board used to look.
 *
 * The deploy is visible from here: index.html names the bundle it loads
 * (./assets/index-<hash>.js) and every build names a new one. Every 15
 * minutes, and when the tab comes back into view (once in 5 minutes at most),
 * index.html is asked for again — a bodiless 304 while nothing was deployed —
 * and compared with the bundle this tab is running. GPMX's web app asks as
 * often (VersionCheckService, 15 minutes and on reopening); the owner,
 * 2026-10-08: 「不能做成每分钟检查一次这样会造成server负担」. It was every 3
 * minutes and on every return after one: a 304 from nginx, never the API, but
 * a deploy waits for nobody, and the return to the tab catches the people at
 * their desks. A newer one is taken
 * without asking only where nobody can be in the middle of something:
 *  - while the tab is out of sight (the person is on WhatsApp or another tab);
 *  - at the next change of page (对话 → 客户表), which becomes a full load;
 *  - at once on the TV board, which nobody types into.
 * Never while a dialog or the customer drawer is open, a box has the cursor, a
 * reply, recording or picture is unsent, a box somebody typed into still holds
 * their words, or a save is on its way. The pages stay mounted behind the one
 * on screen, so that is asked of all of them, not just the one in view. While
 * somebody is looking, one line says so, with 刷新 beside it; the conversation
 * that was open is open again after the reload.
 *
 * lazior-cp does the same in src/v2/fresh.tsx. Off under the dev server, which
 * serves /src/main.tsx rather than a built bundle.
 */

/** How often a tab asks on its own. */
const EVERY_MS = 15 * 60_000;
/** Coming back to the tab asks at once, but not more often than this: alt-tab fires focus many times a minute. */
const SOON_MS = 5 * 60_000;
/** The bundle index.html loads, read the same way off this page and off the server's copy. */
const BUNDLE = 'script[type="module"][src*="/assets/"]';

let stale = false;
const subs = new Set<() => void>();
const subscribe = (f: () => void) => {
  subs.add(f);
  return () => {
    subs.delete(f);
  };
};
const isStale = () => stale;
const markStale = () => {
  if (stale) return;
  stale = true;
  subs.forEach((f) => f());
};
// Dev only (gone from a build): window.__lzFresh() shows the frame's 「有新版本」 at once,
// to look at it — the dev server has no deployed build to be newer than.
if (import.meta.env.DEV) (window as Window & { __lzFresh?: () => void }).__lzFresh = markStale;

/** Work on screen that the page itself says a reload would lose (useHold). */
let holds = 0;

/**
 * Keeps the tab from reloading itself while `on`. For what the page holds but
 * no box shows: a recording, a picked picture, the customer drawer, a sign-in.
 */
export function useHold(on: boolean): void {
  useEffect(() => {
    if (!on) return;
    holds++;
    return () => {
      holds--;
    };
  }, [on]);
}

/** Boxes whose words are somebody's work. Search and date boxes only filter a list. */
const WORDS = new Set(["text", "email", "tel", "url", "number", "password"]);
/** Inputs that take a click, not typing. */
const CLICKED = new Set(["checkbox", "radio", "button", "submit", "reset", "range", "file", "color", "image", "hidden"]);
/** Boxes typed into since the page loaded; dropped once they leave the page. */
const typed = new Set<HTMLInputElement | HTMLTextAreaElement>();
const noteTyping = (e: Event) => {
  const t = e.target;
  if (t instanceof HTMLTextAreaElement || (t instanceof HTMLInputElement && WORDS.has(t.type))) typed.add(t);
};

/** Somebody is in the middle of something a reload would throw away. */
function busy(): boolean {
  if (holds > 0 || writesInFlight() > 0) return true;
  if (document.querySelector('[role="dialog"], .wa-overlay')) return true;
  const a = document.activeElement;
  if (a instanceof HTMLTextAreaElement || a instanceof HTMLSelectElement) return true;
  if (a instanceof HTMLInputElement && !CLICKED.has(a.type)) return true;
  if (a instanceof HTMLElement && a.isContentEditable) return true;
  for (const el of typed) {
    if (!el.isConnected) typed.delete(el);
    else if (el.value.trim() !== "") return true;
  }
  return false;
}

/*
 * Where somebody was, carried across a reload this file makes — sessionStorage,
 * so this tab only — and read once by the load that follows. An ordinary F5
 * finds nothing and opens as it always has.
 */
const PLACE_KEY = "lazior_fresh_place";
/** The build the reload was for: a reload that came back on the old one is not repeated by itself. */
const FOR_KEY = "_build";
const kept = new Map<string, string>();
const place: Record<string, string> = (() => {
  try {
    const v = sessionStorage.getItem(PLACE_KEY);
    sessionStorage.removeItem(PLACE_KEY);
    return v ? (JSON.parse(v) as Record<string, string>) : {};
  } catch {
    return {};
  }
})();
const reloadedFor = place[FOR_KEY] ?? "";

/**
 * A reload asked of a copy the browser keeps of its own can come back on the
 * old build: the bar stayed, 刷新 brought it back (owner, 2026-10-08, on a
 * phone: 「为什么我用手机用sales.lazior.com左上角上面都显示“刷新”？」). Once that has
 * happened, the next reload asks for the page under an address no copy is
 * kept for, and the load that follows puts the address back as it was.
 */
const BUST = "_fresh";
try {
  const u = new URL(location.href);
  if (u.searchParams.has(BUST)) {
    u.searchParams.delete(BUST);
    history.replaceState(history.state, "", u.pathname + u.search + u.hash);
  }
} catch {
  /* an address this cannot read is left as it is */
}

/**
 * What `key` held when this tab reloaded itself, handed out once: a chat
 * opened again later starts empty, never with a reply already sent from it.
 */
export function usePlaceBefore(key: string): string | undefined {
  const [value] = useState(() => place[key]);
  useEffect(() => {
    delete place[key];
  }, [key]);
  return value;
}

/** Asks for `value` back after the console reloads itself for a new build. */
export function useKeepPlace(key: string, value: string | null): void {
  useEffect(() => {
    if (value === null) return;
    kept.set(key, value);
    return () => {
      kept.delete(key);
    };
  }, [key, value]);
}

let deployed = "";
function reload(): void {
  try {
    sessionStorage.setItem(PLACE_KEY, JSON.stringify({ ...Object.fromEntries(kept), [FOR_KEY]: deployed }));
  } catch {
    /* storage refused: the page still reloads, it just opens on the list */
  }
  if (reloadedFor && reloadedFor !== ownBundle()) {
    const u = new URL(location.href);
    u.searchParams.set(BUST, String(Date.now()));
    location.replace(u.toString());
    return;
  }
  location.reload();
}

/** The bundle this tab is running, as index.html named it when the tab loaded; "" under the dev server. */
const ownBundle = () => document.querySelector(BUNDLE)?.getAttribute("src") ?? "";

/**
 * The bundle the server names now; "" when that cannot be told (offline, an
 * error page). Asked as a revalidation of the browser's own copy: index.html is
 * no-cache with an ETag (nginx.conf), so while nothing was deployed the server
 * answers 304 with no body — some 300 bytes a question, and no work for the API.
 */
async function deployedBundle(): Promise<string> {
  const res = await fetch(location.pathname, { cache: "no-cache" });
  if (!res.ok) return "";
  const page = new DOMParser().parseFromString(await res.text(), "text/html");
  return page.querySelector(BUNDLE)?.getAttribute("src") ?? "";
}

/**
 * Whether that bundle is actually handed out yet. Mid-rollout a pod already
 * names the new build while another still answers its files with a 404
 * (nginx.conf): reloading then leaves a blank page where the console was.
 */
async function served(src: string): Promise<boolean> {
  const res = await fetch(new URL(src, location.href), { method: "HEAD", cache: "no-store" });
  return res.ok;
}

const pause = (ms: number) => new Promise((r) => window.setTimeout(r, ms));

/** Until the next build's board shows today's figures, or ten seconds. */
async function boardDrawn(box: HTMLElement): Promise<void> {
  for (let i = 0; i < 50; i++) {
    const figure = box.querySelector(".lv .lv-tv");
    if (figure && figure.textContent !== "—") {
      await pause(500); // its pictures, a moment more
      return;
    }
    await pause(200);
  }
}

let swapping = false;

/**
 * The entries this page has run. An address runs once in a page (the browser
 * keeps one module per address), so a build this page ran before — a rollback —
 * cannot be swapped in, only reloaded. The page also keeps every build it ran,
 * some 2.5 MB each (measured on the harness, 2026-10-20): after MAX_SWAPS a
 * reload gives that back, full screen or not.
 */
const MAX_SWAPS = 30;
type Ran = Window & { __laziorRan?: Set<string> };
const ranHere = (was: string) => ((window as Ran).__laziorRan ??= new Set(was ? [new URL(was, location.href).href] : []));

/**
 * The TV board in full screen takes a new build without a reload (boot.ts): a
 * reload left full screen, and only a click can enter it again, with nobody at
 * the meeting room's screen to click (owner, 2026-10-20: 「他会自己跳出全屏模式」).
 * The next build is loaded and drawn behind this one, and only once its board
 * shows its figures does this one go. Anything that fails before that leaves
 * this build on screen, to try again at the next check.
 */
async function swapIn(src: string): Promise<void> {
  if (swapping) return;
  swapping = true;
  const script = document.querySelector(BUNDLE);
  const was = script?.getAttribute("src") ?? "";
  const added: HTMLLinkElement[] = [];
  let box: HTMLDivElement | null = null;
  let leave: ReturnType<typeof takeLeaving> | null = null;
  let done = false;
  try {
    const res = await fetch(location.pathname, { cache: "no-cache" });
    if (!res.ok) return;
    const html = await res.text();
    const page = new DOMParser().parseFromString(html, "text/html");
    // Deployed again since it was found: the next check finds that one.
    if (page.querySelector(BUNDLE)?.getAttribute("src") !== src) return;
    const entry = new URL(src, location.href).href;
    const ran = ranHere(was);
    if (ran.has(entry) || ran.size > MAX_SWAPS) {
      done = true;
      reload();
      return;
    }
    const code = await (await fetch(entry)).text();
    // Its stylesheets, loaded before anything on screen changes.
    const have = new Set([...document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')].map((l) => l.href));
    for (const l of page.querySelectorAll('link[rel="stylesheet"][href]')) {
      const href = new URL(l.getAttribute("href") ?? "", location.href).href;
      if (have.has(href)) continue;
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      added.push(link);
      const loaded = await new Promise<boolean>((ok) => {
        link.onload = () => ok(true);
        link.onerror = () => ok(false);
        document.head.appendChild(link);
      });
      if (!loaded) return;
    }
    // The next build draws behind this one, unseen and untouchable.
    box = document.createElement("div");
    box.style.cssText = "position:fixed;inset:0;opacity:0;pointer-events:none;z-index:-1";
    document.body.appendChild(box);
    leave = takeLeaving();
    mountNextIn(box);
    script?.setAttribute("src", src);
    // This file may be the next build's too (one module): it starts with nothing waiting.
    stale = false;
    deployed = "";
    if (!(await import(/* @vite-ignore */ entry).then(() => true).catch(() => false))) return;
    ran.add(entry);
    await boardDrawn(box);
    // The handover: this build's React and sound go, its box with them.
    leave.run();
    document.getElementById("root")?.remove();
    box.removeAttribute("style");
    box.id = "root";
    done = true;
    // This build's stylesheets that the next one names nowhere (index.html, or
    // the list of files its pages load, in its entry): a rule it dropped must not stay.
    for (const l of document.querySelectorAll<HTMLLinkElement>('link[rel="stylesheet"]')) {
      const file = l.href.split("/").pop() ?? "";
      if (l.href.includes("/assets/") && !html.includes(file) && !code.includes(file)) l.remove();
    }
  } catch {
    /* offline, or mid-deploy: this build stays */
  } finally {
    if (!done) {
      box?.remove();
      mountNextIn(null);
      added.forEach((l) => l.remove());
      script?.setAttribute("src", was);
      leave?.giveBack();
      stale = true;
      deployed = src;
    }
    swapping = false;
  }
}

/**
 * Runs once, in App: watches for a newer build and takes it where that cannot
 * cost anybody their work. `view` is the page on screen; `anytime` is the TV
 * board, which takes it as soon as nothing is in the way.
 */
export function useFreshBuild(view: string, anytime: boolean): void {
  const anytimeRef = useRef(anytime);
  useEffect(() => {
    anytimeRef.current = anytime;
  }, [anytime]);

  useEffect(() => {
    const mine = ownBundle();
    if (!mine) return;
    // This load was a reload for a build it did not get (a stale page in some
    // cache, or the old pod answered): the bar still says so, but the tab does
    // not go round again on its own.
    const missed = reloadedFor !== mine ? reloadedFor : "";
    document.addEventListener("input", noteTyping, true);
    let checking = false;
    let asked = 0;
    const take = () => {
      if (!stale || deployed === missed || busy()) return;
      // The TV board in full screen: the new build comes in without a reload.
      if (anytimeRef.current && document.fullscreenElement) void swapIn(deployed);
      else if (anytimeRef.current || document.visibilityState === "hidden") reload();
    };
    // `soon`: a focus or a return to the tab rather than the timer.
    const check = async (soon = false) => {
      if (stale) return take();
      if (checking || (soon && Date.now() - asked < SOON_MS)) return;
      checking = true;
      asked = Date.now();
      try {
        const now = await deployedBundle();
        if (now && now !== mine && (await served(now))) {
          deployed = now;
          markStale();
          take();
        }
      } catch {
        /* offline, or the server is mid-deploy: the next round asks again */
      } finally {
        checking = false;
      }
    };
    const onShow = () => {
      if (document.visibilityState === "visible") void check(true);
      else take();
    };
    const timer = window.setInterval(() => void check(), EVERY_MS);
    document.addEventListener("visibilitychange", onShow);
    window.addEventListener("focus", onShow);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onShow);
      window.removeEventListener("focus", onShow);
      document.removeEventListener("input", noteTyping, true);
    };
  }, []);

  // A change of page while a newer build waits: the page opens from the new
  // build. A moment later, so App has put the page in the address and a
  // conversation the customer table asked to open is there to be carried.
  const firstView = useRef(true);
  useEffect(() => {
    if (firstView.current) {
      firstView.current = false;
      return;
    }
    if (!stale) return;
    const t = window.setTimeout(() => {
      if (!busy()) reload();
    }, 100);
    return () => window.clearTimeout(t);
  }, [view]);
}

/**
 * Whether the console was updated under the open page. The frame says so where
 * it takes no room (prototype v2.5): the logo row turns into 「有新版本 · 刷新」
 * on a computer; on a phone, a dot on 更多 and the first row of its sheet. It
 * used to be a yellow line across the top, and on 对话 it pushed the typing box
 * off the screen (owner 10/18).
 */
export function useStaleBuild(): boolean {
  return useSyncExternalStore(subscribe, isStale);
}

/** 刷新: into the new build now. */
export function reloadNow(): void {
  reload();
}
